import { useImportTableDefinition } from '@front/hook/Api/ImportTableDefinition';
import { useImportTask } from '@front/hook/Api/ImportTask';
import { useNavigateBack } from '@front/hook/useNavigateBack';
import { useToast } from '@plaidev/sour-react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useProject } from 'auth-lib-front';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { DomainV2 } from 'troppus-common/domain';

import ImportTableDefinition = DomainV2.Import.ImportTableDefinition;
import ImportTableDefinitionRevision = DomainV2.Import.ImportTableDefinitionRevision;
import ImportTask = DomainV2.Import.ImportTask;

import type { ImportTableDefinitionUpdateProps } from '@front/repository/Api/ImportTableDefinition/interface';

export type DetailFormValues = {
  displayName: string;
  description: string;
};

const routeApi = getRouteApi('/table-definitions/$id');

export function useTableDefinitionDetail() {
  const navigate = useNavigate();
  const goBackWithFallback = useNavigateBack();
  const toast = useToast();
  const { project } = useProject();
  const { id: idParam } = routeApi.useParams();

  const tableDefinitionId = useMemo(() => ImportTableDefinition.Id.FromString(idParam), [idParam]);

  const { data, isPending } = useImportTableDefinition.Get({ projectId: project.id, id: tableDefinitionId });
  const updateMutation = useImportTableDefinition.Update();
  const { data: revisions = [], isPending: revisionsPending } = useImportTableDefinition.ListRevisions({
    projectId: project.id,
    id: tableDefinitionId,
  });
  const { data: importTasks = [], isPending: importTasksPending } = useImportTask.List(project.id, {
    importTableDefinitionId: tableDefinitionId,
    pollIntervalMs: 10_000,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<DetailFormValues>({
    defaultValues: { displayName: '', description: '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!data) return;
    reset({
      displayName: data.definition.displayName,
      description: data.definition.description ?? '',
    });
  }, [data, reset]);

  const creationTaskByRevisionId = useMemo(() => {
    const map = new Map<ImportTableDefinitionRevision.Id, ImportTask>();
    for (const task of [...importTasks].sort((a, b) => a.createdAt.valueOf() - b.createdAt.valueOf())) {
      if (!map.has(task.importTableDefinitionRevisionId)) {
        map.set(task.importTableDefinitionRevisionId, task);
      }
    }
    return map;
  }, [importTasks]);

  /** 同じテーブル定義で API と同様に未完了（Requested / Started）のみ */
  const incompleteImportTasks = useMemo(() => {
    return importTasks
      .filter((t) => t.status === 'Requested' || t.status === 'Started')
      .sort((a, b) => b.createdAt.valueOf() - a.createdAt.valueOf());
  }, [importTasks]);
  const hasIncompleteImportTask = incompleteImportTasks.length > 0;

  const latestFinishedImportTask = useMemo(() => {
    return importTasks
      .filter((t) => t.status === 'Finished')
      .sort((a, b) => {
        const aTime = (a.finishedAt ?? a.createdAt).valueOf();
        const bTime = (b.finishedAt ?? b.createdAt).valueOf();
        return bTime - aTime;
      })[0];
  }, [importTasks]);

  const onSubmit = handleSubmit(async (values) => {
    if (!data) return;
    const trimmedName = values.displayName.trim();
    const trimmedDescription = values.description.trim();
    const updateProps: ImportTableDefinitionUpdateProps = {
      displayName: trimmedName,
      description: trimmedDescription === '' ? undefined : trimmedDescription,
    };
    try {
      await updateMutation.mutateAsync({
        projectId: project.id,
        id: tableDefinitionId,
        updateProps,
      });
      reset({
        displayName: trimmedName,
        description: trimmedDescription,
      });
      toast.success('保存しました');
    } catch (err) {
      const message = err instanceof Error ? err.message : '更新に失敗しました';
      toast.error(message);
    }
  });

  const navigateBack = () => goBackWithFallback({ to: '/table-definitions' });
  const navigateToUpload = () => void navigate({ to: '/table-definitions/$id/upload', params: { id: tableDefinitionId } });
  const navigateToTask = (taskId: ImportTask.Id) =>
    void navigate({
      to: '/tasks/$id',
      params: { id: taskId },
    });
  const navigateToTaskList = (revisionId: ImportTableDefinitionRevision.Id) =>
    void navigate({
      to: '/tasks',
      search: {
        importTableDefinitionId: tableDefinitionId,
        importTableDefinitionRevisionId: revisionId,
      } as { importTableDefinitionId: string; importTableDefinitionRevisionId: string },
    });

  return {
    data,
    isPending,
    control,
    isDirty,
    isValid,
    updateMutationIsPending: updateMutation.isPending,
    onSubmit,
    navigateBack,
    navigateToUpload,
    revisions,
    revisionsPending: revisionsPending || importTasksPending,
    creationTaskByRevisionId,
    navigateToTask,
    navigateToTaskList,
    incompleteImportTasks,
    hasIncompleteImportTask,
    latestFinishedImportTask,
    importTasksPending,
  };
}
